import { useContext, useEffect, useMemo, useState } from 'react'
import { PendingStatus } from '@components'
import { submissionContext } from '@contexts'
import { Submission } from '@global/types'
import { useAppSelector } from '@redux/hooks'
import { api } from 'api'
import { DownloadDataset, EditSelectRows, EditSubmission, OverwriteDataset } from './actionButtons'
import { DownloadOption } from './types'
import { ActionType, useDatasetDataContext } from '../../datasetData/context'
import { t } from '@lingui/core/macro'
import { IDatasetMetadataResponse } from '@global/api/types'

interface Props {
  datasetId: number | string
  datasetMeta?: IDatasetMetadataResponse
  manualEditDisabled: boolean
  numSelectedRows: number
  manualEditRows: () => Promise<void>
  correctErroredRows?: () => Promise<void>
  shouldShowEditConfirmModal: boolean
}

/**
 * Header for the submission details page -> codes/details/:id/data
 */
export const SubmissionDetailsHeader = ({
  datasetId,
  datasetMeta,
  manualEditDisabled,
  numSelectedRows,
  manualEditRows,
  correctErroredRows,
  shouldShowEditConfirmModal,
}: Props) => {
  const { selectiveEditModeEnabled, dispatch: dispatchContext } = useDatasetDataContext()
  const { byId } = useContext(submissionContext)
  const submissionTemplateById = useAppSelector(state => state.Datasets.submissionTemplatesById)
  const template = submissionTemplateById[datasetMeta?.template_id]
  const { integrations } = template || {}
  const [downloadOptionsLoading, setDownloadOptionsLoading] = useState<boolean>(true)
  const [downloadOptions, setDownloadOptions] = useState<DownloadOption[]>([])

  const submission: Submission = byId[datasetId]
  const submissionStatus = submission?.status || submission?.latest_submission_status

  const canOverwrite = Boolean(datasetMeta?.can_overwrite)
  const canResubmit = Boolean(datasetMeta?.can_resubmit)
  const canEditSubmissions = Boolean(datasetMeta?.can_user_edit_submissions)
  const isPendingRollup = Boolean(submission?.is_pending_rollup)
  const currentRowCount = datasetMeta?.count ?? 0
  const isNotAwaitingApproval = submissionStatus !== PendingStatus.AWAITING_APPROVAL.valueOf()

  const showEditSelectRows = isPendingRollup || (isNotAwaitingApproval && canEditSubmissions)
  const showEditSubmission = canEditSubmissions && canResubmit && !isPendingRollup

  useEffect(() => {
    dispatchContext({ type: ActionType.editMode, payload: { isEnabled: false } })

    void api
      .get<
        { file_type: string; id: string; name: string }[]
      >(`/api/v1/integration-assignments/dataset/${datasetId}`)
      .then(({ data }) => {
        const downOpts: DownloadOption[] = [
          { id: 'default', name: t`Default`, type: '', exportConfigurationId: null },
        ]
        data.forEach(dl => {
          if (dl.file_type) {
            JSON.parse(dl.file_type).forEach(ty => {
              downOpts.push({
                id: dl.id,
                name: `${dl.name}.${ty}`,
                type: ty,
                exportConfigurationId: null,
              })
            })
          }
        })

        const aimiaSupportedOptions = downOpts.filter(opt => opt.type !== 'json')

        const options = isPendingRollup ? aimiaSupportedOptions : downOpts

        const downloadOptionsWithCorrectExportConfigurationId = options.map(downloadOption => ({
          ...downloadOption,
          exportConfigurationId: integrations?.find(
            integration => integration.id === downloadOption.id,
          )?.config_hash,
        }))

        setDownloadOptions(downloadOptionsWithCorrectExportConfigurationId)
        setDownloadOptionsLoading(false)
      })
  }, [isPendingRollup, integrations])

  const handleCorrectErroredRows = useMemo(() => {
    if (submissionStatus !== PendingStatus.ERROR) {
      return
    }
    return correctErroredRows
  }, [correctErroredRows, submissionStatus])

  return (
    <>
      {showEditSelectRows && (
        <EditSelectRows
          shouldShowEditConfirmModal={shouldShowEditConfirmModal}
          manualEditDisabled={manualEditDisabled}
          numSelectedRows={numSelectedRows}
          manualEditRows={manualEditRows}
          correctErroredRows={handleCorrectErroredRows}
        />
      )}

      {!selectiveEditModeEnabled && (
        <>
          {showEditSubmission && (
            <EditSubmission
              datasetId={datasetId}
              currentRowCount={currentRowCount}
              shouldShowEditConfirmModal={shouldShowEditConfirmModal}
            />
          )}
          {canOverwrite && <OverwriteDataset />}
          <DownloadDataset
            downloadOptions={downloadOptions}
            download={(id, type) => void downloadCSV(id, type)}
            isPendingRollup={isPendingRollup}
            loading={downloadOptionsLoading}
          />
        </>
      )}
    </>
  )

  async function downloadCSV(id: number | string, type: string) {
    const { data } = await api.get<{ file: string }>(
      `/api/v1/integration-assignments/${id}/getCSVDownload/${datasetMeta?.id}/${type}`,
    )
    window.location.href = data.file
  }
}
